import {
  Text,
  Checkbox,
  DirectionalHint,
  Dropdown,
  ICommandBarItemProps,
  IDropdownOption,
  Icon,
  IconButton,
  Label,
  Stack,
  TextField,
  TooltipDelay,
  TooltipHost,
} from '@fluentui/react';
import * as React from 'react';
import { Rule, RuleSetTypeComplex, RuleSetTypeSingle } from '../../../../../Common/DataOwnerModels/RuleSetRecord';
import { DataOwnerRequestContext } from '../../../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import { useContext, useState } from 'react';
import { useId } from '@fluentui/react-hooks';
import { v4 as uuidv4 } from 'uuid';
import * as styled from '../../DataOwnerManagement.styled';

export type RuleManagementProps = {
  ruleSetId?: string;
};

export function RuleManagement(props: RuleManagementProps): React.ReactElement {
  // Data Owner Request Context
  const {dataOwnerRequestDetails} = useContext(DataOwnerRequestContext);
  const currentRuleSetIndex = dataOwnerRequestDetails.ruleSets?.findIndex((x) => x.id == props.ruleSetId);
  const currentRuleSet = dataOwnerRequestDetails?.ruleSets[currentRuleSetIndex];

  // Command Bar State
  const [isRemoveRulesButtonDisabled, setIsRemoveRulesButtonDisabled] = useState<boolean>(true);

  // Form State
  const [ruleIdCheckedList, setRuleIdCheckedList] = useState<string[]>([]);
  const [ruleIdExpandSectionList, setRuleIdExpandSectionList] = useState<string[]>([]);

  // Tooltip State
  const ruleNameTooltipId = useId('ruleNameTooltip');
  const ruleErrorMsgTooltipId = useId('ruleErrorMsgTooltip');
  const ruleIsOptionalTooltipId = useId('ruleIsOptionalTooltip');
  const ruleExpressionTooltipId = useId('ruleExpressionTooltip');
  const ruleDataQualityDimensionTooltipId = useId('ruleDataQualityDimensionTooltip');

  // Field Data
  const booleanFlagOptions: IDropdownOption[] = [
    { key: 'true', text: 'True' },
    { key: 'false', text: 'False' },
  ];
  // Note: Key and Text should be of same alphabetical case
  const dataQualityDimensionOptions: IDropdownOption[] = [
    { key: 'Accuracy', text: 'Accuracy' },
    { key: 'Timeliness', text: 'Timeliness' },
    { key: 'Consistency', text: 'Consistency' },
    { key: 'Completeness', text: 'Completeness' },
    { key: 'Uniqueness', text: 'Uniqueness' },
    { key: 'Conformity', text: 'Conformity' },
  ];

  const _commandBarAddRuleItems: ICommandBarItemProps[] = [
    {
      key: 'addRule',
      text: 'Add Rule',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        const newRule: Rule = {
          ruleId: uuidv4(),
          ruleName: 'New Rule',
          dataQuality: [],
        };
        setRuleIdExpandSectionList([...ruleIdCheckedList, newRule.ruleId]);

        if (currentRuleSet.rules == null) {
          dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, { ...currentRuleSet, rules: [newRule] });
          return;
        }

        dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
          ...currentRuleSet,
          rules: [...currentRuleSet.rules, newRule],
        });
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
  ];
  const _commandBarDeleteRuleItems: ICommandBarItemProps[] = [
    {
      key: 'removeRule',
      text: 'Remove Rule',
      iconProps: { iconName: 'Delete' },
      disabled: isRemoveRulesButtonDisabled,
      onClick: (event, item) => {
        ruleIdCheckedList?.map((x) => {
          dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
            ...currentRuleSet,
            rules: currentRuleSet.rules.filter((y) => y.ruleId != x),
          });
        });

        const newRuleIdExpandSectionList: string[] = ruleIdExpandSectionList.filter((ruleIdDropdown) => {
          return ruleIdCheckedList.includes(ruleIdDropdown) == false;
        });
        setRuleIdExpandSectionList(newRuleIdExpandSectionList);
        setRuleIdCheckedList([]);
        setIsRemoveRulesButtonDisabled(true);
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 16px',
        },
      },
    },
  ];

  const _onChangeDropdown = (ruleObj: Rule): void => {
    const newRuleIdExpandSectionList: string[] = JSON.parse(
      JSON.stringify(ruleIdExpandSectionList) // deep copy
    );
    if (ruleIdExpandSectionList.includes(ruleObj.ruleId)) {
      const cdmIdDropdownIndex: number = ruleIdExpandSectionList.indexOf(ruleObj.ruleId);
      newRuleIdExpandSectionList.splice(cdmIdDropdownIndex, 1);
    } else {
      newRuleIdExpandSectionList.push(ruleObj.ruleId);
    }
    setRuleIdExpandSectionList(newRuleIdExpandSectionList);
  };

  const _onChangeRuleSelectCheckBox = (checked: boolean, ruleObj: Rule) => {
    const newRuleIdCheckedList: string[] = JSON.parse(
      JSON.stringify(ruleIdCheckedList) // deep copy
    );
    if (checked) {
      newRuleIdCheckedList.push(ruleObj.ruleId);
    } else {
      const removeIndex = newRuleIdCheckedList.indexOf(ruleObj.ruleId);
      newRuleIdCheckedList.splice(removeIndex, 1);
    }

    if (newRuleIdCheckedList.length > 0) {
      setIsRemoveRulesButtonDisabled(false);
    } else {
      setIsRemoveRulesButtonDisabled(true);
    }

    setRuleIdCheckedList(newRuleIdCheckedList);
  };

  const _renderRuleTitleDropdown = (ruleObj: Rule): JSX.Element => {
    const isChecked = ruleIdCheckedList.includes(ruleObj.ruleId);
    const isSectionExpand = ruleIdExpandSectionList.includes(ruleObj.ruleId);
    return (
      <styled.SingleRuleContainer>
        <styled.CheckTextIconContainer>
          <styled.CheckTextContainer>
            <Checkbox
              key={ruleObj.ruleId}
              id={ruleObj.ruleId}
              ariaLabel={`${ruleObj.ruleName} Check Box`}
              checked={isChecked}
              onChange={(ev, checked): void => _onChangeRuleSelectCheckBox(checked, ruleObj)}
              styles={styled.checkBoxStyles}
            />
            <styled.SingleRuleTextStyled>{ruleObj.ruleName}</styled.SingleRuleTextStyled>
          </styled.CheckTextContainer>
          {isSectionExpand && (
            <Icon
              data-testid="contract-rule"
              aria-label="Contract Rule"
              iconName="ChevronUp"
              className={styled.dropdownIconClass}
              onClick={() => _onChangeDropdown(ruleObj)}
            />
          )}
          {!isSectionExpand && (
            <Icon
              data-testid="expand-rule"
              aria-label="Expand Rule"
              iconName="ChevronDown"
              className={styled.dropdownIconClass}
              onClick={() => _onChangeDropdown(ruleObj)}
            />
          )}
        </styled.CheckTextIconContainer>
        <styled.LineDivider />
      </styled.SingleRuleContainer>
    );
  };

  // Informational Toop tip
  const onRenderLabelName = () => {
    return (
      <styled.LabelContainer>
        <Label>Rule Name</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={ruleNameTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>This field holds Custom Rule Name, used for easy of identification.</Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            id={'RuleNameHelpIcon'}
            iconProps={{ iconName: 'Info' }}
            aria-describedby={ruleNameTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelErrorMsg = () => {
    return (
      <styled.LabelContainer>
        <Label>Error Message</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={ruleErrorMsgTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    This field should be configured with custom Error message that will be shown if rule validation
                    fails on the data.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            iconProps={{ iconName: 'Info' }}
            aria-describedby={ruleErrorMsgTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelExpression = () => {
    return (
      <styled.LabelContainer>
        <Label>Expression</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={ruleExpressionTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>This field should be configured with the expression to validate the data.</Text>
                  <Text> - If rule set type is "Single", expression should given be C# language.</Text>
                  <Text> - If rule set type is "Complex", expression should given be Python language.</Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            id={'helpIcon'}
            iconProps={{ iconName: 'Info' }}
            aria-describedby={ruleExpressionTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelIsOptional = () => {
    return (
      <styled.LabelContainer>
        <Label>Optional</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={ruleIsOptionalTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>This field indicates whether the rule validation is required.</Text>
                  <Text>
                    - If this field is configured as 'True', this rule validation is optional, and data that fails won't
                    be pushed to Failed data store.
                  </Text>
                  <Text>
                    - If this field is configured as 'False', this rule validation is required. If the rule validation
                    fails, the data will be pushed to Failed data store.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            iconProps={{ iconName: 'Info' }}
            aria-describedby={ruleIsOptionalTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelDataQualityDimension = () => {
    return (
      <styled.LabelContainer>
        <Label>Data Quality Dimension</Label>
        <TooltipHost
          id={ruleDataQualityDimensionTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    This field represents defined standards dimension or metrics to determine the quality of data.
                  </Text>
                  <Text>
                    - Accuracy: The amount of certainity ascribed to data, that it correctly reflects the fact, truth,
                    event or real-life object it is intended to model.
                  </Text>
                  <Text> - Timeliness: Mesures compliance to all expectations of data with respect to time.</Text>
                  <Text>
                    - Consistency: Describes the degree of agreement, harmony, or compatibility of the data with a point
                    of reference.
                  </Text>
                  <Text>
                    - Completeness: Mesures the presence of all the necessary or appropriate parts; being entire,
                    finished, total.
                  </Text>
                  <Text>
                    - Uniqueness: Measures the degree to which a real-life object, a concept or an event is represented
                    only once.
                  </Text>
                  <Text>
                    - Validity: Measures agreement with a source of 'correct' information such as reference data,
                    certified or master data or another authoritative source.
                  </Text>
                  <Text>
                    - Confirmity: Measures the degree to which data values comply with attributes associated with the
                    data element (metatdata) i.e. its data type, precision, format patterns, domain ranges and
                    underlying storage formats.
                  </Text>
                  <Text>
                    - Precision: The right level of data value granularity, e.g. numeric rounding, number of decimal
                    places.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            id={'helpIcon'}
            iconProps={{ iconName: 'Info' }}
            aria-describedby={ruleDataQualityDimensionTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  return (
    <div id="edit-request-ruleset-rules">
      {(currentRuleSet?.rules == null || currentRuleSet?.rules?.length == 0) && (
        <div>
          <p>&nbsp;</p>
          <h4> Add a new Rules to get started...</h4>
          <styled.StyledCommandBar
            data-testid="edit-ruleset-add-rules"
            items={_commandBarAddRuleItems}
            ariaLabel="Command Bar"
          />
        </div>
      )}
      {currentRuleSet?.rules?.length > 0 && (
        <styled.StyledCommandBar
          items={_commandBarAddRuleItems.concat(_commandBarDeleteRuleItems)}
          ariaLabel="Command Bar"
        />
      )}
      {currentRuleSet?.rules?.map((ruleObj: Rule, ruleIndex: number) => (
        <styled.SingleContainer key={ruleObj.ruleId}>
          {_renderRuleTitleDropdown(ruleObj)}
          {ruleIdExpandSectionList.includes(ruleObj.ruleId) && (
            <Stack tokens={{ childrenGap: 16 }}>
              <TextField
                id="ruleName"
                data-testid={'new-rule-rulename' + ruleIndex}
                placeholder="Enter Custom Rule Name"
                onRenderLabel={onRenderLabelName}
                value={ruleObj?.ruleName}
                onChange={(event, newValue) => {
                  const newRulesList: Rule[] = currentRuleSet?.rules?.map((temprule, tempindex) => {
                    if (tempindex == ruleIndex) {
                      return { ...temprule, ruleName: newValue };
                    }
                    return temprule;
                  });
                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                    ...currentRuleSet,
                    rules: newRulesList,
                  });
                }}
                validateOnLoad={false}
                validateOnFocusOut={true}
              />
              <TextField
                id="errorMessage"
                data-testid={'new-rule-errormsg' + ruleIndex}
                placeholder="Enter Error Message to display"
                onRenderLabel={onRenderLabelErrorMsg}
                value={ruleObj?.errorMessage}
                onChange={(event, newValue) => {
                  const newRulesList: Rule[] = currentRuleSet?.rules?.map((temprule, tempindex) => {
                    if (tempindex == ruleIndex) {
                      return { ...temprule, errorMessage: newValue };
                    }
                    return temprule;
                  });
                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                    ...currentRuleSet,
                    rules: newRulesList,
                  });
                }}
                validateOnLoad={false}
                validateOnFocusOut={true}
                autoAdjustHeight={true}
              />
              <TextField
                id="expression"
                data-testid={'new-rule-expression' + ruleIndex}
                onRenderLabel={onRenderLabelExpression}
                placeholder={
                  currentRuleSet?.ruleSetType == RuleSetTypeComplex
                    ? 'Enter Python Expression'
                    : currentRuleSet?.ruleSetType == RuleSetTypeSingle
                    ? 'Enter C# Expression'
                    : 'Enter Expression in C#/Python'
                }
                value={
                  currentRuleSet?.ruleSetType == RuleSetTypeComplex
                    ? ruleObj?.expression?.compiledExpression?.python
                    : ruleObj?.expression?.compiledExpression?.cSharp
                }
                onChange={(event, newValue) => {
                  const newRulesList: Rule[] = currentRuleSet?.rules?.map((temprule, tempindex) => {
                    if (tempindex == ruleIndex) {
                      if (currentRuleSet?.ruleSetType == RuleSetTypeComplex) {
                        if (temprule.expression == null || temprule.expression.compiledExpression) {
                          return {
                            ...temprule,
                            expression: {
                              compiledExpression: { python: newValue },
                            },
                          };
                        }
                        return {
                          ...temprule,
                          expression: {
                            ...temprule?.expression,
                            compiledExpression: { ...temprule.expression.compiledExpression, python: newValue },
                          },
                        };
                      } else if (currentRuleSet?.ruleSetType == RuleSetTypeSingle) {
                        if (temprule.expression == null || temprule.expression.compiledExpression) {
                          return {
                            ...temprule,
                            expression: {
                              compiledExpression: { cSharp: newValue },
                            },
                          };
                        }
                        return {
                          ...temprule,
                          expression: {
                            ...temprule?.expression,
                            compiledExpression: { ...temprule.expression.compiledExpression, cSharp: newValue },
                          },
                        };
                      }
                    }
                    return temprule;
                  });
                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                    ...currentRuleSet,
                    rules: newRulesList,
                  });
                }}
                validateOnLoad={false}
                validateOnFocusOut={true}
                multiline={true}
                autoAdjustHeight={true}
              />
              <Dropdown
                id="optional"
                placeholder="Is this optional Rule?"
                data-testid={'new-rule-optional' + ruleIndex}
                ariaLabel="Optional"
                onRenderLabel={onRenderLabelIsOptional}
                selectedKey={ruleObj?.optional?.toString()}
                options={booleanFlagOptions}
                onChange={(event, newValue) => {
                  const newRulesList: Rule[] = currentRuleSet?.rules?.map((temprule, tempindex) => {
                    if (tempindex == ruleIndex) {
                      return { ...temprule, optional: newValue.key == 'true' };
                    }
                    return temprule;
                  });
                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                    ...currentRuleSet,
                    rules: newRulesList,
                  });
                }}
                multiSelect={false}
              />
              <Dropdown
                id="dataQualityDimension"
                placeholder="Select dimensions"
                data-testid={'new-rule-dataQualityDimension' + ruleIndex}
                onRenderLabel={onRenderLabelDataQualityDimension}
                ariaLabel="Data Quality"
                selectedKeys={ruleObj?.dataQuality}
                options={dataQualityDimensionOptions}
                onChange={(event, newValue) => {
                  const newRulesList: Rule[] = currentRuleSet?.rules?.map((temprule, tempindex) => {
                    if (tempindex == ruleIndex) {
                      if (newValue && newValue.selected) {
                        if (temprule.dataQuality == null) {
                          return { ...temprule, dataQuality: [newValue.text] };
                        }
                        const addDataQuality = [...temprule.dataQuality, newValue.text];
                        return { ...temprule, dataQuality: addDataQuality };
                      } else if (newValue && !newValue.selected) {
                        const removeDataQuality = temprule.dataQuality.filter((x) => x != newValue.text);
                        return { ...temprule, dataQuality: removeDataQuality };
                      }
                    }
                    return temprule;
                  });
                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                    ...currentRuleSet,
                    rules: newRulesList,
                  });
                }}
                multiSelect={true}
              />
            </Stack>
          )}
          <br />
        </styled.SingleContainer>
      ))}
    </div>
  );
}
